import { Euro, Search } from '@project-lary/react-material-symbols';
import LoadingIndicator from 'gcs-common/components/LoadingIndicator/LoadingIndicator';
import {
  useGetConnectionMembers,
  useUpdateConnectionMemberMutation,
} from '../../clients/api/entities/connection-member/connection-member-api';
import OpPermissions from '../../helper/opPermissionsHelper';
import InfoBox from '../InfoBox/InfoBox';
import MemberPermissionsItem from '../MemberPermissionItem/MemberPermissionItem';

interface MemberPermissionsProps {
  userId: string;
  houseId: string;
}

const MemberPermissions = (props: MemberPermissionsProps) => {
  const { userId, houseId } = props;
  const { data, isPending, isError } = useGetConnectionMembers({
    userId,
    houseId,
  });

  const updateConnectionMemberMutation = useUpdateConnectionMemberMutation();

  if (isPending) {
    return <LoadingIndicator />;
  }

  const connectionMember = data?.[0];

  if (!connectionMember) {
    // biome-ignore lint/complexity/noUselessFragments: intentionally renders nothing here
    return <></>;
  }

  if (isError) {
    return <div>Es ist ein Fehler aufgetreten.</div>;
  }

  const opAccount = connectionMember?.opAccount;

  if (!opAccount) {
    return (
      <div className="flex flex-col pt-4">
        <div className="flex flex-row items-center justify-between px-3 py-2">
          <div className="text-base-medium">ONLINE PLUS</div>
        </div>
        <InfoBox text="Eine ONLINE PLUS Verbindung für diesen Nutzer ist noch nicht eingerichtet." />
      </div>
    );
  }

  if (opAccount.process_data_source === 'GCOnlinePlus') {
    return (
      <div className="flex flex-col pt-4">
        <div className="flex flex-row items-center justify-between px-3 py-2">
          <div className="text-base-medium">ONLINE PLUS</div>
        </div>
        <InfoBox text="Die Berechtigungen für diesen Kunden können in Online Plus verwaltet werden." />
      </div>
    );
  }

  const updatePermissions = (permissionType: OpPermissions) => async (toggleValue: boolean) => {
    await updateConnectionMemberMutation.mutateAsync({
      params: {
        path: {
          connection_member_id: connectionMember.id,
        },
      },
      body: {
        [OpPermissions.PROCESSES_ENABLED]: opAccount.search_processes_enabled,
        [OpPermissions.SHOW_BRUTTO_PRICE]: opAccount.processes_show_brutto_price,
        [OpPermissions.SHOW_NETTO_PRICE]: opAccount.processes_show_netto_price,
        [permissionType]: toggleValue,
      },
    });
  };

  return (
    <div className="flex flex-col pt-4">
      <div className="flex flex-row items-center justify-between px-3 py-2">
        <div className="text-base-medium">ONLINE PLUS</div>
      </div>
      {opAccount.is_permissions_editable === false && (
        <InfoBox text="Bitte kontaktiere deinen BEEM-Keyuser, um Änderungen vorzunehmen." />
      )}
      <div className="flex flex-col overflow-hidden rounded-xl bg-white [&>div:not(:last-child)]:border-0 [&>div:not(:last-child)]:border-neutral-6 [&>div:not(:last-child)]:border-b-thin [&>div:not(:last-child)]:border-solid">
        <MemberPermissionsItem
          title="Vorgangssuche anzeigen"
          Icon={Search}
          toggleEnabled={!!opAccount?.search_processes_enabled}
          onToggle={updatePermissions(OpPermissions.PROCESSES_ENABLED)}
          testIdAttribute="searchProcessesToggle"
          disabled={!opAccount.is_permissions_editable}
        />
        <MemberPermissionsItem
          title="Nettopreise anzeigen"
          Icon={Euro}
          toggleEnabled={!!opAccount?.processes_show_netto_price}
          onToggle={updatePermissions(OpPermissions.SHOW_NETTO_PRICE)}
          testIdAttribute="showNettoPriceToggle"
          disabled={!opAccount.is_permissions_editable}
        />
        <MemberPermissionsItem
          title="Listenpreise anzeigen"
          Icon={Euro}
          toggleEnabled={!!opAccount?.processes_show_brutto_price}
          onToggle={updatePermissions(OpPermissions.SHOW_BRUTTO_PRICE)}
          testIdAttribute="showBruttoPriceToggle"
          disabled={!opAccount.is_permissions_editable}
        />
      </div>
    </div>
  );
};

export default MemberPermissions;
